import React, { useState, useEffect, useContext } from "react"
import { Utensils, ExternalLink } from "lucide-react"

import { BookingContext } from "../../../utils/providers/isolated-booking"
import { db } from "../../../utils/firebase"

import styles from "./practical.module.scss"

/**
 * Nearby restaurants — agent-picked spots near each hotel on the booking,
 * shown as cards with a photo, the hotel they're near, and a website link.
 */
export default function Nearby() {
	const { bookingID, hotelsOrder } = useContext(BookingContext)
	const [places, setPlaces] = useState([])

	useEffect(() => {
		if (!bookingID || !hotelsOrder?.length) return
		let cancelled = false

		const load = async () => {
			const perHotel = await Promise.all(
				hotelsOrder.map(async (hid) => {
					const resortID = (await db.doc(`bookings/${bookingID}/hotels/${hid}`).get()).data()?.resortID
					if (!resortID) return []

					const destinationName = (await db.doc(`hotels/${resortID}`).get()).data()?.name || ""
					const nearbyDocs = await db.collection(`hotels/${resortID}/nearby`).get()

					return Promise.all(
						nearbyDocs.docs.map(async (nearbyDoc) => {
							const data = nearbyDoc.data()
							const imgs = await db.collection(`hotels/${resortID}/nearby/${nearbyDoc.id}/images`).limit(1).get()
							return {
								id: `${resortID}-${nearbyDoc.id}`,
								name: data.name || "",
								website: data.website || "",
								near: destinationName,
								image: imgs.empty ? "" : imgs.docs[0].data().public_url,
							}
						}),
					)
				}),
			)

			if (!cancelled) setPlaces(perHotel.flat())
		}

		load().catch((err) => console.log("Nearby load failed:", err))
		return () => {
			cancelled = true
		}
	}, [bookingID, hotelsOrder])

	return (
		<div className={styles.page}>
			<header className={styles.head}>
				<h1>Nearby restaurants</h1>
				<p>A few favourites near your hotel, handpicked by our agents.</p>
			</header>

			{places.length === 0 ? (
				<div className={styles.empty}>We don't have any nearby recommendations for this booking yet.</div>
			) : (
				<div className={styles.cards}>
					{places.map((place) => (
						<div
							key={place.id}
							className={styles.card}>
							{place.image ? (
								<div
									className={styles.cardImage}
									style={{ backgroundImage: `url("${place.image}")` }}
								/>
							) : null}
							<div className={styles.nearbyBody}>
								<span className={styles.nearbyKicker}>
									<Utensils size={13} />
									Restaurant
								</span>
								<h3 className={styles.nearbyName}>{place.name}</h3>
								{place.near ? <p className={styles.nearbyNear}>Near {place.near}</p> : null}
								{place.website ? (
									<a
										href={place.website}
										target="_blank"
										rel="noreferrer"
										className={styles.nearbyLink}>
										Visit website
										<ExternalLink size={14} />
									</a>
								) : null}
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	)
}
